import { useState, useCallback, useMemo, useEffect } from "react";
import { Grid } from "@mui/material";
import { makeStyles } from "tss-react/mui";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { nbNO, enUS } from "@mui/x-data-grid-pro/locales";
import EntityEditDialog from "../inputs/EntityEditDialog";
import { useTranslation } from "react-i18next";
import { usePersistentFilter } from "../../../hooks/usePersistentFilter";
import { PAGESIZE, INITIAL_STATE, COMPONENTS, SX, autoHeight } from "./constants";
import { useDevice } from "../../../providers/DeviceProvider";

const useStyles = makeStyles()(
  (theme) => ({
    gridInnerContainer: {
      flexGrow: 1,
      overflowX: "hidden",
      height: "auto",
      "& .MuiDataGrid-virtualScrollerContent": {
        minHeight: "240px !important"
      }
    },
    gridInnerContainerAutoHeight: {
      flexGrow: 1,
      overflowX: "hidden",
      height: "auto",
    },
    table: {
      border: "none",
      minHeight: "100%",
      "& .MuiDataGrid-row": {
        "&:hover": {
          background: theme.palette.action.hover
        }
      },
      "& .MuiDataGrid-cell:focus": {
        outline: "none"
      },
      "& ::-webkit-scrollbar": {
        width: "6px",
        height: "6px",
      },
      "& ::-webkit-scrollbar-thumb": {
        borderRadius: "4px",
        background: "rgba(155, 155, 155, 0.25)",
      },
    },
    tableMobile: {
      "& .MuiDataGrid-columnHeaderTitleContainer [title=\"Sorter\"]": {
        display: "none"
      },
      "& .MuiDataGrid-columnHeader--sorted .MuiDataGrid-columnHeaderTitleContainer [title=\"Sorter\"]": {
        display: "flex"
      }
    }
  })
);

export default function SimpleTable(props) {
  const { persistentFilterRef, tableId, onStateChange, updating, onDelete, onUpdate, loading, rows, columns, fields, emptyStateTitle, emptyStateDescription, editTitle, onRowClick, apiRef, grow, getRowClassName, checkboxSelection, onSelectionModelChange, selectionModel, disablePaging, defaultSettings } = props;
  const { classes } = useStyles();
  const { i18n } = useTranslation();
  const { isMobile } = useDevice();

  const [dialog, setDialog] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  const showLoader = loading && rows.length === 0;

  const [paginationModel, setPaginationModel] = useState({
    pageSize: PAGESIZE,
    page: 0
  });

  const resetPaging = useCallback(() => {
    setPaginationModel(paging => ({ ...paging, page: 0 }));
  }, []);

  const {
    sortModel,
    filterOverride,
    setSortModel,
    setFilterOverride,
    onReset,
    isPinned,
    visibleColumns,
    setVisibleColumns,
    pinnedColumns,
    setPinnedColumns,
  } = usePersistentFilter(tableId, resetPaging, defaultSettings);

  useEffect(() => {
    if (!!persistentFilterRef) {
      persistentFilterRef.current = {
        onReset
      }
    }
  }, [onReset])

  

  const onHandleRowClick = useCallback((params, ev) => {
    if (window.getSelection()?.type !== "Range") {
      ev.preventDefault();
      if (!!onRowClick) {
        onRowClick(params, ev);
      } else {
        setSelectedRow(rows.find(t => t.id === params.id));
        setDialog(true);
      }
    }
  }, [rows, onRowClick]);

  const onDialogClose = useCallback(() => {
    setDialog(null);
  }, []);

  const onHandleUpdate = useCallback(async (values, updatedFields) => {
    onUpdate(selectedRow, values, updatedFields);
    setSelectedRow(null);
    setDialog(null);
  }, [onUpdate, selectedRow]);

  const onHandleDelete = useCallback(async () => {
    onDelete(selectedRow);
    setSelectedRow(null);
    setDialog(null);
  }, [selectedRow, onDelete]);

  return (
    (<Grid item xs={12} container>
      <div className={grow ? classes.gridInnerContainerAutoHeight : classes.gridInnerContainer}>
        <DataGridPro
          checkboxSelection={checkboxSelection}
          disableColumnReorder
          apiRef={apiRef}
          sx={SX}
          className={`${classes.table} ${isMobile ? classes.tableMobile : ""}`}
          rows={rows}
          loading={showLoader}
          columns={columns}
          getRowHeight={autoHeight}
          onRowClick={onHandleRowClick}
          initialState={INITIAL_STATE}
          getRowClassName={getRowClassName}
          columnVisibilityModel={visibleColumns}
          onColumnVisibilityModelChange={setVisibleColumns}
          autoHeight
          pinnedColumns={pinnedColumns}
          onPinnedColumnsChange={setPinnedColumns}
          onStateChange={onStateChange}
          

          onRowSelectionModelChange={onSelectionModelChange}
          rowSelectionModel={selectionModel}

          filterModel={!!filterOverride ? filterOverride : undefined}
          onFilterModelChange={setFilterOverride}

          sortModel={sortModel}
          onSortModelChange={setSortModel}
          disableRowSelectionOnClick

          pageSizeOptions={useMemo(() => rows.length < PAGESIZE ? [Math.max(rows.length, 1), PAGESIZE, 50, 100, 150] : [PAGESIZE, 50, 100, 150], [rows.length])}
          paginationMode="client"
          pagination={!disablePaging}
          paginationModel={!disablePaging ? paginationModel : undefined}
          onPaginationModelChange={!disablePaging ? setPaginationModel : undefined}

          slots={COMPONENTS}
          slotProps={{
            noRowsOverlay: {
              title: emptyStateTitle,
              description: emptyStateDescription,
            },
            toolbar: {
              canSearch: true,
              isPinned,
              tableId,
              onReset,
            }
          }}
          localeText={(i18n.language === "no" ? nbNO : enUS)?.components.MuiDataGrid.defaultProps.localeText}
        />
      </div>
      {
        dialog ? (
          <EntityEditDialog
            editTitle={editTitle}
            canDelete={!!selectedRow}
            entity={selectedRow}
            updating={updating}
            fields={fields}
            open={dialog}
            onCancel={onDialogClose}
            onUpdate={onHandleUpdate}
            onDelete={onHandleDelete}
          />
        ) : null
      }
    </Grid>)
  );
}







